import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { activityQueryKeys } from 'services/api/activity';
import { adminClientsQueryKeys } from 'services/api/admin/clients';
import { adminDenialListItemQueryKeys } from 'services/api/admin/denialListItems';
import { adminDenialListsQueryKeys } from 'services/api/admin/denialLists';
import { adminHighCostMedicationListItemQueryKeys } from 'services/api/admin/highCostMedicationListItems';
import { adminHighCostMedicationListsQueryKeys } from 'services/api/admin/highCostMedicationLists';
import { admissionQueryKeys } from 'services/api/admissions';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { chatQueryKeys } from 'services/api/chat';
import { denialListItemQueryKeys } from 'services/api/denialListItems';
import { dischargeCalendarQueryKeys } from 'services/api/dischargeCalendar';
import { episodeQueryKeys } from 'services/api/episode';
import { highCostMedicationListItemQueryKeys } from 'services/api/highCostMedicationListItems';
import { highCostMedicationTierQueryKeys } from 'services/api/highCostMedicationTiers';
import { progressChartQueryKeys } from 'services/api/locationEpisode/progressChart';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { referralSourceQueryKeys } from 'services/api/referralSources';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { summarizationQueryKeys } from 'services/api/summarizations';

export type InvalidateOptions = {
  episode?: boolean | Record<string, any>;
  episodes?: boolean;
  locationEpisode?: boolean | Record<string, any>;
  authorizations?: boolean | Record<string, any>;
  authorization?: boolean | Record<string, any>;
  referrals?: boolean;
  referral?: boolean | Record<string, any>;
  activities?: boolean;
  attachments?: boolean | Record<string, any>;
  attachmentsCount?: boolean | Record<string, any>;
  progressChart?: boolean;
  summarizations?: boolean;
  summarization?: boolean | Record<string, any>;
  admissions?: boolean;
  denialListItems?: boolean;
  referralSources?: boolean;
  highCostMedicationListItems?: boolean;
  highCostMedicationTiers?: boolean;
  adminDenialListItems?: boolean;
  adminDenialLists?: boolean;
  denialListEligibleClients?: boolean;
  adminHighCostMedicationLists?: boolean;
  adminHighCostMedicationListItems?: boolean;
  highCostMedListEligibleClients?: boolean;
  chatConversations?: boolean | Record<string, any>;
  chatMessages?: boolean | Record<string, any>;
};

const getParams = (option: boolean | Record<string, any>) => (typeof option === 'object' ? { ...option } : {});

export default function useInvalidateQueries() {
  const queryClient = useQueryClient();

  return useCallback(
    ({
      episode = false,
      episodes = false,
      locationEpisode = false,
      authorizations = false,
      authorization = false,
      referrals = false,
      referral = false,
      activities = false,
      attachments = false,
      attachmentsCount = false,
      progressChart = false,
      summarizations = false,
      summarization = false,
      admissions = false,
      denialListItems = false,
      referralSources = false,
      highCostMedicationListItems = false,
      highCostMedicationTiers = false,
      adminDenialListItems = false,
      adminDenialLists = false,
      denialListEligibleClients = false,
      adminHighCostMedicationLists = false,
      adminHighCostMedicationListItems = false,
      highCostMedListEligibleClients = false,
      chatConversations = false,
      chatMessages = false,
    }: InvalidateOptions = {}) => {
      if (episode) queryClient.invalidateQueries({ queryKey: episodeQueryKeys.show(getParams(episode)) });
      if (episodes) {
        queryClient.invalidateQueries({ queryKey: episodeQueryKeys.base });
        queryClient.invalidateQueries({ queryKey: dischargeCalendarQueryKeys.base });
      }
      if (locationEpisode) {
        queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show(getParams(locationEpisode)) });
        if (progressChart)
          queryClient.invalidateQueries({ queryKey: progressChartQueryKeys.show(getParams(locationEpisode).id) });
      }
      if (authorizations)
        queryClient.invalidateQueries({ queryKey: authorizationQueryKeys.index(getParams(authorizations)) });
      if (authorization)
        queryClient.invalidateQueries({ queryKey: authorizationQueryKeys.show(getParams(authorization)) });
      if (referrals) queryClient.invalidateQueries({ queryKey: referralQueryKeys.base }); // invalidates referrals list page AND all patient story referrals
      if (referral) queryClient.invalidateQueries({ queryKey: referralQueryKeys.show(getParams(referral)) });
      if (activities) queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
      if (attachments) queryClient.invalidateQueries({ queryKey: attachmentsQueryKeys.index(getParams(attachments)) });
      if (attachmentsCount)
        queryClient.invalidateQueries({ queryKey: attachmentsQueryKeys.count(getParams(attachmentsCount)) });
      if (summarizations) queryClient.invalidateQueries({ queryKey: summarizationQueryKeys.base });
      if (summarization)
        queryClient.invalidateQueries({ queryKey: summarizationQueryKeys.show(getParams(summarization)) });
      if (admissions) queryClient.invalidateQueries({ queryKey: admissionQueryKeys.base });
      if (denialListItems) queryClient.invalidateQueries({ queryKey: denialListItemQueryKeys.base });
      if (referralSources) queryClient.invalidateQueries({ queryKey: referralSourceQueryKeys.base });
      if (highCostMedicationListItems)
        queryClient.invalidateQueries({ queryKey: highCostMedicationListItemQueryKeys.base });
      // The customer cost tier key is a rollup of what the medications carry, so any item write can
      // bring a tier into being or retire the last reference to one. The customer page fetches tiers
      // separately (it never sees the list entity), so unlike the admin surface it gets no fresh
      // tiers for free when the items query refetches.
      if (highCostMedicationTiers) queryClient.invalidateQueries({ queryKey: highCostMedicationTierQueryKeys.base });
      if (adminDenialListItems) queryClient.invalidateQueries({ queryKey: adminDenialListItemQueryKeys.base });
      // Refreshes the admin tab's patient_count + "Last Updated" after an add/delete.
      if (adminDenialLists) queryClient.invalidateQueries({ queryKey: adminDenialListsQueryKeys.base });
      // A created/deleted list changes which clients are eligible in the "Add New List" picker.
      // That dropdown is a DISABLED infinite query (useInfiniteOptions) that fetches only on
      // menu-open and only when not already fetched — so invalidateQueries can't refresh it
      // (disabled queries don't auto-refetch and isFetched stays true). Reset it instead so the
      // next open refetches from scratch.
      if (denialListEligibleClients) queryClient.resetQueries({ queryKey: adminClientsQueryKeys.denialListEligible });
      // Refreshes the admin tab's medication count and "Last Updated" after a list is created or
      // removed.
      if (adminHighCostMedicationLists)
        queryClient.invalidateQueries({ queryKey: adminHighCostMedicationListsQueryKeys.base });
      if (adminHighCostMedicationListItems)
        queryClient.invalidateQueries({ queryKey: adminHighCostMedicationListItemQueryKeys.base });
      // Same disabled-infinite-query reasoning as the denial picker above: reset, don't invalidate.
      if (highCostMedListEligibleClients)
        queryClient.resetQueries({ queryKey: adminClientsQueryKeys.highCostMedListEligible });
      if (chatConversations) {
        const { workflowId } = getParams(chatConversations);
        if (workflowId) {
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.index(workflowId) });
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.unreadCounts(workflowId) });
        } else {
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.base });
        }
      }
      if (chatMessages) {
        const { conversationId } = getParams(chatMessages);
        queryClient.invalidateQueries({
          queryKey: conversationId ? chatQueryKeys.messages(conversationId) : chatQueryKeys.messagesBase,
        });
      }
    },
    [queryClient]
  );
}
